/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

/* eslint-disable no-use-before-define,no-param-reassign,no-mixed-operators,class-methods-use-this
 */
import $ from 'jquery';
// @ts-ignore
import biComponentErrorFactory from 'js-sdk/src/common/bi/error/biComponentErrorFactory';
// @ts-ignore
import request from 'js-sdk/src/common/transport/request';
import _ from 'underscore';
import { IMultiLevelDataService } from './ServicesTypes';
import { IState } from '../store/types/StateTypes';
import {
    IMultiLevelDataServiceDataLayout,
    IMultiLevelDataset,
    IMultiLevelDatasetNode,
    NodeType
} from '../store/types/MultiLevelDatasetTypes';

type ExecutionState = 'new' | 'existing' | 'deleted';

type Execution = {
    layout: IMultiLevelDataServiceDataLayout,
    state: ExecutionState,
    id: JQueryDeferred<string>,
    calls: {
        [_: string]: JQueryDeferred<any>,
    },
    server:string
}

function areLayoutsSame(oldDataLayout: IMultiLevelDataServiceDataLayout, newDataLayout: IMultiLevelDataServiceDataLayout) {
    if (!oldDataLayout
        || oldDataLayout.distinctFields !== newDataLayout.distinctFields
        || oldDataLayout.order !== newDataLayout.order
        || newDataLayout.limit !== oldDataLayout.limit
        || oldDataLayout.filters.expression !== newDataLayout.filters.expression
        || newDataLayout.filters.parameters !== oldDataLayout.filters.parameters) {
        return false;
    }

    let isChanged = newDataLayout.details.reduce((changed, detail, index) => (
        changed
        || oldDataLayout.details[index] && (
            detail.field !== oldDataLayout.details[index].field
            || detail.detailType !== oldDataLayout.details[index].detailType
        )
    ), !oldDataLayout);

    isChanged = newDataLayout.aggregations.reduce((changed, aggregation, index) => (
        changed
        || oldDataLayout.details[index] && (
            aggregation.field !== oldDataLayout.aggregations[index].field
            || aggregation.aggregationType !== oldDataLayout.aggregations[index].aggregationType
            || aggregation.aggregationFunction !== oldDataLayout.aggregations[index].aggregationFunction
            || aggregation.timeBalanceFunctionName !== oldDataLayout.aggregations[index].timeBalanceFunctionName
        )
    ), isChanged);

    return !newDataLayout.groups.reduce((changed, group, index) => (
        changed || (group !== oldDataLayout.groups[index])
    ), isChanged);
}

function convertNode(dataNode: any, groupLabels: string[][], currentDepth: number, maxDepth: number) {
    const { group, all } = dataNode;
    const node: IMultiLevelDatasetNode = {
        label: null,
        nodeType: NodeType.ROOT,
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        children: getChildrenNodes(dataNode, groupLabels, currentDepth + 1, maxDepth),
        values: []
    };

    if (group) {
        node.nodeType = NodeType.GROUP;
        node.label = groupLabels[currentDepth][group.memberIdx];

        if (group.data) {
            node.values = group.data;
        }
    }

    if (all) {
        if (all.data) {
            node.values = all.data;
        }
    }

    return node;
}

function getChildrenNodes(dataNode: any, groupLabels: string[][], currentDepth: number, maxDepth: number,): IMultiLevelDatasetNode[] {
    let { group } = dataNode;
    const { all } = dataNode;
    let result;

    if (!group) {
        group = all;
    }

    if (group && group.children && group.children.length) {
        if (group.children[0].group) {
            result = group.children.map((child: any) => (
                convertNode(child, groupLabels, currentDepth, maxDepth)
            ));
        } else if (group.children[0].detail) {
            const { detail } = group.children[0];
            result = detail.data.map((detailData: any) => ({
                label: null,
                nodeType: NodeType.DETAIL,
                children: [],
                values: detailData
            }
            ));
        }
    }

    return result || [];
}

function convertResponse(data: any): IMultiLevelDatasetNode {
    const { dataset: { levelDataNodes, levels } } = data;
    const groupLabels: string[][] = levels.map((level: any) => (level.group ? level.group.members : []));
    const dataRoot = levelDataNodes ? levelDataNodes[0] : { all: { children: [] } };

    return convertNode(dataRoot, groupLabels, 0, levels.length - 1)
}

function generateQuery(dataLayout: IMultiLevelDataServiceDataLayout, startIndex: number, limit: number, state: IState) {
    const result = {
        params: { offset: [startIndex], pageSize: [limit] },
        dataSource: { reference: state.resourceURI },
        query: {
            select: {},
            groupBy: [
                { allGroup: {} }, ...dataLayout.groups.map((groupId) => (
                    { group: { field: groupId } }
                ))
            ]
        }
    } as any;

    if (dataLayout.details.length) {
        const fields = dataLayout.details.map((detail) => ({ field: detail.field }));

        if (dataLayout.distinctFields) {
            result.query.select.distinctFields = fields;
        } else {
            result.query.select.fields = fields;
        }
    }

    if (dataLayout.aggregations.length) {
        result.query.select.aggregations = dataLayout.aggregations.map((agg) => ({
            type: agg.aggregationType,
            functionName: agg.aggregationFunction,
            fieldRef: agg.field,
            timeBalanceFunctionName: agg.timeBalanceFunctionName || 'TimeBalanceDefault'
        }));
    }

    if (dataLayout.filters.expression || dataLayout.filters.parameters.length) {
        result.query.where = {
            filterExpression: dataLayout.filters.expression,
            parameters: dataLayout.filters.parameters.map((param: any) => ({
                name: param.name,
                expression: param.value
            }))
        }
    }

    if (dataLayout.order.length) {
        result.query.orderBy = dataLayout.order;
    }

    if (!_.isUndefined(dataLayout.limit) && dataLayout.limit >= 0) {
        result.query.limit = Math.min(dataLayout.limit, 15);
    }

    return result;
}

function paginationCode(startIndex: number, limit: number) {
    return `${startIndex}x${limit}`;
}

class QueryExecutionMultiLevelDataService implements IMultiLevelDataService {
    data: JQueryDeferred<IMultiLevelDataset>;

    executions: Execution[];

    constructor() {
        this.data = $.Deferred().resolve();
        this.executions = [];
    }

    private addExecution(exec: Execution) {
        const exisiting = this.executions.filter((exe) => (exe.state === 'existing'));

        this.executions = [exec].concat(this.executions.filter((exe) => (exe.state === 'new')));

        setTimeout(() => {
            exisiting.forEach(this.removeExecution)
        });
    }

    private createNewExecution(dataLayout: IMultiLevelDataServiceDataLayout, startIndex: number, limit: number, state: IState): JQueryDeferred<any> {
        const query: any = generateQuery(dataLayout, startIndex, limit, state);

        const exec: Execution = {
            layout: dataLayout,
            state: 'new',
            id: $.Deferred(),
            calls: {},
            server: state.server
        };

        this.addExecution(exec);

        return request({
            url: state.server.concat(`/rest_v2/queryExecutions${state?.refreshData ? '?freshData=true' : ''}`),
            headers: {
                Accept: 'application/multiLevelData+json',
                'Content-Type': 'application/execution.multiLevelQuery+json'
            },
            data: JSON.stringify(query),
            method: 'POST',
            processData: false
        }).done((data: any, status: string, xhr: XMLHttpRequest) => {
            const location = xhr.getResponseHeader('Content-Location');
            if (location) {
                exec.state = 'existing';
                exec.id.resolve(location);
                exec.server = state.server;
            }
        });
    }

    private callExistingExecution(id: string, startIndex: number, limit: number, state: IState) {
        return request({
            url: `${state.server}/${id}?offset=${startIndex}&pageSize=${limit}`,
            headers: {
                Accept: 'application/multiLevelData+json'
            },
            processData: false
        });
    }

    private getAsyncResult(dataLayout: IMultiLevelDataServiceDataLayout, startIndex: number, limit: number, state: IState): JQueryDeferred<any> | null {
        let result: JQueryDeferred<any> | null = null;

        const exec: Execution | undefined = this.executions.find((execution) => (areLayoutsSame(execution.layout, dataLayout)));

        if (exec) {
            const code = paginationCode(startIndex, limit);

            if (!exec.calls[code]) {
                exec.calls[code] = $.Deferred();
                result = exec.calls[code];

                exec.id.done((id) => {
                    this.callExistingExecution(id, startIndex, limit, state).done((data: any, status: string, xhr: XMLHttpRequest) => {
                        result!.resolve(data, status, xhr);
                    }).fail((a: any, b: any, c: any) => {
                        result!.reject(a, b, c);
                    });
                });
            }
        } else {
            result = this.createNewExecution(dataLayout, startIndex, limit, state);
        }

        return result;
    }

    private removeExecution(execution: Execution) {
        const res = $.Deferred();
        execution.id.done((id) => {
            request({
                url: `${execution.server}/${id.split('/data')[0]}`,
                method: 'DELETE'
            }).always(() => {
                res.resolve();
            });
        })

        return res;
    }

    reset() {
        const res = $.Deferred();
        const removals = this.executions.map(this.removeExecution);
        this.executions = [];

        $.when(...removals).always(() => {
            res.resolve();
        });

        return res;
    }

    loadData(dataLayout: IMultiLevelDataServiceDataLayout, startIndex: number, limit: number, state: IState): JQueryDeferred<IMultiLevelDataset> {
        const asyncResult = this.getAsyncResult(dataLayout, startIndex, limit, state);
        const dfd = $.Deferred();

        if (asyncResult) {
            this.data = dfd;
            asyncResult.done((data: any) => {
                this.data.resolve({
                    summary: {
                        ...dataLayout,
                        empty: !data.totalCounts,
                        complete: data.dataset.counts < limit
                    } as any,
                    data: convertResponse(data)
                });
            }).fail((error) => {
                error = biComponentErrorFactory.requestError(error);
                this.data.reject(error);
            });
        }

        return dfd;
    }
}

export { QueryExecutionMultiLevelDataService, generateQuery, areLayoutsSame }
